import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { AlertTriangle, CheckCircle, Loader2, XCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import axios from "axios";
import API from "@/apiConfig";

export default function RentalCancelPage() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cancelling, setCancelling] = useState(false);
  const [cancelled, setCancelled] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    axios.get(`${API}/rentals/cancel/${token}`)
      .then((r) => {
        if (r.data.status === "cancelled") {
          setCancelled(true);
          setResult({ refund_note: "This booking has already been cancelled." });
        } else {
          setInfo(r.data);
        }
      })
      .catch((e) => setError(e.response?.data?.detail || "Cancellation link not found or expired."))
      .finally(() => setLoading(false));
  }, [token]);

  const handleCancel = async () => {
    setCancelling(true);
    try {
      const res = await axios.post(`${API}/rentals/cancel/${token}`);
      setCancelled(true);
      setResult(res.data);
      toast.success("Booking cancelled.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to cancel. Please try again.");
    } finally {
      setCancelling(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-bone flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-gold" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-bone flex items-center justify-center px-4">
        <div className="max-w-md text-center">
          <AlertTriangle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h1 className="font-heading text-2xl text-navy mb-2">Link Unavailable</h1>
          <p className="text-gray-500">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-bone" data-testid="cancel-page">
      <div className="bg-navy py-8 text-center">
        <h1 className="font-heading text-2xl sm:text-3xl text-white">Word Alive Global Center</h1>
        <p className="text-gold text-sm mt-1">Community Room — Cancellation</p>
      </div>

      <div className="max-w-lg mx-auto px-4 sm:px-6 py-10">
        {cancelled ? (
          <div className="text-center py-10" data-testid="cancel-confirmed">
            <XCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
            <h2 className="font-heading text-2xl text-navy mb-3">Booking Cancelled</h2>
            <div className="bg-red-50 border border-red-200 rounded-sm p-4 text-left mb-4">
              <p className="text-sm text-gray-700">{result?.refund_note}</p>
            </div>
            <p className="text-gray-500 text-sm">A confirmation email has been sent. For questions, contact communityroom@wordalivetx.com.</p>
          </div>
        ) : (
          <>
            <div className="bg-white rounded-sm border border-bone-dark p-6 mb-6">
              <h2 className="font-heading text-xl text-navy mb-4">Cancel This Booking?</h2>
              <div className="space-y-2 text-sm text-gray-600 mb-4">
                <div className="flex justify-between"><span className="text-gray-500">Renter</span><span className="text-navy font-medium">{info.name}</span></div>
                <div className="flex justify-between"><span className="text-gray-500">Event</span><span className="text-navy font-medium">{info.event_name}</span></div>
                <div className="flex justify-between"><span className="text-gray-500">Date</span><span className="text-navy font-medium">{info.event_date}</span></div>
                {info.event_time && <div className="flex justify-between"><span className="text-gray-500">Time</span><span className="text-navy">{info.event_time}</span></div>}
              </div>
            </div>

            <div className={`rounded-sm border p-4 mb-6 ${info.eligible_for_fee_refund ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`} data-testid="refund-info">
              <h3 className="font-heading text-base text-navy mb-2">Refund Policy</h3>
              {info.eligible_for_fee_refund ? (
                <div className="space-y-2 text-sm">
                  <div className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-green-600 mt-0.5 shrink-0" />
                    <span className="text-gray-700"><strong>Rental fee</strong> ({info.event_date.includes("Sat") ? "$250" : "$150"}) — <strong className="text-green-700">Refundable</strong> (cancelled {Math.round(info.hours_until_event)}h before event)</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <XCircle className="h-4 w-4 text-red-500 mt-0.5 shrink-0" />
                    <span className="text-gray-700"><strong>$50 deposit</strong> — <strong className="text-red-600">Non-refundable</strong></span>
                  </div>
                </div>
              ) : (
                <div className="space-y-2 text-sm">
                  <div className="flex items-start gap-2">
                    <XCircle className="h-4 w-4 text-red-500 mt-0.5 shrink-0" />
                    <span className="text-gray-700"><strong>No refund</strong> — Cancelled within 48 hours of event ({Math.round(info.hours_until_event)}h remaining)</span>
                  </div>
                  <p className="text-gray-500 text-xs mt-1">Both the $50 deposit and rental fee are non-refundable when cancelled within 48 hours.</p>
                </div>
              )}
            </div>

            <div className="flex gap-3">
              <a href="/rental" className="flex-1">
                <Button variant="outline" className="w-full border-bone-dark text-navy">Keep Booking</Button>
              </a>
              <Button
                data-testid="confirm-cancel-btn"
                className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                onClick={handleCancel}
                disabled={cancelling}
              >
                {cancelling && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                {cancelling ? "Cancelling..." : "Cancel Booking"}
              </Button>
            </div>
          </>
        )}
      </div>

      <div className="bg-navy py-4 text-center">
        <p className="text-gold text-xs">1128 W Panola Street, Carthage, TX 75633</p>
      </div>
    </div>
  );
}
